import {
  ChevronUpIcon,
  ChevronDownIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline';
import Lottie from 'lottie-react';
import noServicetypeAnimation from '@/assets/lottie/noServicetypeAnimation.json';
import { Button } from '@shared/components/Buttons';
import { ButtonVariant } from '@shared/components/Buttons/types';
import { TabToggle } from '@shared/components/TabToggle';
import { Tooltip } from '@shared/components/Tooltip';
import {
  AVAILABLE_SERVICES_LABELS as L,
  RAISE_SERVICE_REQUEST_LABELS,
} from '../../constants/labels';
import type { ServiceTypeListItem } from '../../types/serviceTypeTypes';
import { useAvailableServiceList, type TabId } from './useAvailableServiceList';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import MyRequestsTab from './MyRequestsTab';
import { ListCardShimmer } from '@shared/components/ListCardShimmer';

const TABS: { id: TabId; label: string }[] = [
  { id: 'available', label: L.TAB_AVAILABLE },
  { id: 'my-requests', label: L.TAB_MY_REQUESTS },
];

const AvailableServiceList: React.FC = () => {
  const {
    items,
    loading,
    hasNextPage,
    isInitialLoading,
    scrollContainerRef,
    sentinelRef,
    activeTab,
    handleTabChange,
    openId,
    toggleAccordion,
    handleRequestService,
  } = useAvailableServiceList();

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();
  const canRequestService =
    !privilegesLoading && privileges.canCreateServiceRequest;

  return (
    <div className="flex flex-col h-full p-6 gap-6 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded">
      <h1 className="text-titleLarge font-normal text-[#181A1B]">
        {L.PAGE_TITLE}
      </h1>

      <TabToggle
        tabs={TABS}
        activeTab={activeTab}
        onChange={handleTabChange}
        ariaLabel={L.PAGE_TITLE}
      />

      {activeTab === 'my-requests' && (
        <MyRequestsTab onRequestService={() => handleTabChange('available')} />
      )}

      {activeTab === 'available' && (
        <div
          ref={scrollContainerRef}
          className="flex flex-col gap-3 flex-1 overflow-y-auto"
        >
          {isInitialLoading && (
            <div data-testid="initial-loader">
              <ListCardShimmer count={8} />
            </div>
          )}

          {!loading && items.length === 0 && (
            <div
              data-testid="empty-state"
              className="flex flex-col items-center justify-center flex-1 py-12"
            >
              <Lottie
                animationData={noServicetypeAnimation}
                loop={false}
                style={{ width: 300, height: 300 }}
              />
              <p className="text-bodyLarge font-medium text-center text-[#474A4C] mt-4">
                {L.EMPTY_NO_SERVICES_TITLE}
              </p>
              <p className="text-bodyLarge text-center text-[#474A4C] mt-2 max-w-sm">
                {L.EMPTY_NO_SERVICES}
              </p>
            </div>
          )}

          {items.map((service, index) => (
            <ServiceCard
              key={service.id}
              index={index}
              service={service}
              isOpen={openId === service.id}
              onToggle={() => toggleAccordion(service.id)}
              onRequest={() => handleRequestService(service.service_name)}
              canRequest={canRequestService}
            />
          ))}

          {hasNextPage && (
            <div
              ref={sentinelRef}
              data-testid="scroll-sentinel"
              className="h-4"
            />
          )}

          {loading && items.length > 0 && (
            <div data-testid="load-more-loader">
              <ListCardShimmer count={1} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ServiceCardProps {
  index: number;
  service: ServiceTypeListItem;
  isOpen: boolean;
  onToggle: () => void;
  onRequest: () => void;
  canRequest: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  index,
  service,
  isOpen,
  onToggle,
  onRequest,
  canRequest,
}) => (
  <div
    data-testid={`service-card-${index}`}
    className="border border-[#DCDDE0] rounded-2xl bg-white overflow-hidden shrink-0 hover:bg-gray-50 transition-colors"
  >
    <div
      className="flex items-center justify-between px-4 py-[18px] cursor-pointer"
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F3F4F6] shrink-0">
          <WrenchIcon className="w-6 h-6 text-[#474A4C]" aria-hidden="true" />
        </span>
        <div className="text-left">
          <p
            data-testid={`service-card-title-${index}`}
            className="font-normal text-[#181A1B] text-sm"
          >
            {service.service_name}
          </p>
          <p className="text-xs text-[#474A4C]">-</p>
        </div>
      </div>
      <Button
        data-testid={`accordion-toggle-${index}`}
        aria-expanded={isOpen}
        aria-label={`${isOpen ? 'Zwiń' : 'Rozwiń'} ${service.service_name}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        icon={
          isOpen ? (
            <ChevronUpIcon className="w-5 h-5" aria-hidden="true" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" aria-hidden="true" />
          )
        }
        showIcon
        className="!bg-transparent !rounded-full focus:!outline-none focus-visible:!ring-2 focus-visible:!ring-[#2A4455]"
        style={{
          backgroundColor: 'transparent',
          color: '#474A4C',
          padding: '6px',
          width: '36px',
          height: '36px',
        }}
      />
    </div>

    {isOpen && (
      <div
        data-testid={`accordion-content-${index}`}
        className="px-4 pb-5 pt-1 cursor-pointer"
        onClick={onToggle}
      >
        <p
          data-testid={`service-card-description-${index}`}
          className="text-[#474A4C] text-sm mb-5"
        >
          {service.description}
        </p>
        <div className="w-fit" onClick={(e) => e.stopPropagation()}>
          {canRequest ? (
            <Button
              label={L.BUTTON_REQUEST}
              variant={ButtonVariant.Primary}
              buttonType="Square"
              size="Small"
              style={{ borderRadius: '8px' }}
              onClick={onRequest}
            />
          ) : (
            <Tooltip
              content={RAISE_SERVICE_REQUEST_LABELS.NO_PRIVILEGE_TOOLTIP}
              forceShow
              side="top"
              contentMaxWidth={280}
              triggerWrapContent
            >
              <Button
                label={L.BUTTON_REQUEST}
                variant={ButtonVariant.Primary}
                buttonType="Square"
                size="Small"
                style={{ borderRadius: '8px' }}
                disabled
              />
            </Tooltip>
          )}
        </div>
      </div>
    )}
  </div>
);

export default AvailableServiceList;
